import { useState, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { BsArrowLeft, BsTelephone, BsEnvelope, BsCalendar, BsCheck, BsLink45Deg, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';

import NewActivityModal from '../../components/activities/NewActivityModal/NewActivityModal';
import AircallDialDropdown from '../../components/aircallEverywhere/AircallDialDropdown';
import MeetingScheduleModal from '../../components/calendar/MeetingScheduleModal/MeetingScheduleModal';
import CrmSearchBar from '../../components/common/CrmSearchBar/CrmSearchBar';
import CompanyActivityPanel from '../../components/company/CompanyActivityPanel/CompanyActivityPanel';
import CompanyHeader from '../../components/company/CompanyHeader/CompanyHeader';
import CompanyInfoCard from '../../components/company/CompanyInfoCard/CompanyInfoCard';
import CompanyTagsCard from '../../components/company/CompanyTagsCard/CompanyTagsCard';
import ContactsCard from '../../components/company/ContactsCard/ContactsCard';
import DealsSection from '../../components/company/DealsSection/DealsSection';
import NextBestAction from '../../components/company/NextBestAction/NextBestAction';
import LossReasonModal from '../../components/pipeline/LossReasonModal/LossReasonModal';
import { contactIdFromHash } from '../../utils/crmPaths';

import useCompanyPageData from './useCompanyPageData';

const CrmCompanyPage = () => {
	const {
		companyId,
		company,
		deals,
		contacts,
		allTags,
		isLoading,
		members,
		allProducts,
		allBundles,
		defaultPipeline,
		defaultStages,
		stagesByPipeline,
		isAircallConnected,
		dialLoading,
		openDeals,
		pipelineValue,
		activeDealId,
		setActiveDealId,
		activeDeal,
		ownerMember,
		aiNextActionEnabled,
		companyFieldDefs,
		companyMutation,
		softDeleteMutation,
		handleAircallDial,
		handleCreateDeal,
		handleDealUpdate,
		handleStageTransition,
		handleDealDelete,
		handleCreateContact,
		handleContactUpdate,
		handleContactDelete,
		handleCompanyDelete,
		lossPrompt,
		lossSubmitting,
		confirmLossTransition,
		setLossPrompt,
	} = useCompanyPageData();

	const location = useLocation();
	const navigate = useNavigate();
	const highlightedContactId = contactIdFromHash(location.hash);
	const backTo = location.state?.backTo;
	const [showNewActivity, setShowNewActivity] = useState(false);
	const [showMeetingModal, setShowMeetingModal] = useState(false);
	const [linkCopied, setLinkCopied] = useState(false);
	const [dealDeleteTarget, setDealDeleteTarget] = useState(null);
	const [dealDeleting, setDealDeleting] = useState(false);
	const [companyDeleteOpen, setCompanyDeleteOpen] = useState(false);

	useEffect(() => {
		if (!isLoading && company && location.hash) {
			const id = location.hash.slice(1);
			const el = document.getElementById(id);
			if (el) el.scrollIntoView({ behavior: 'smooth' });
		}
	}, [isLoading, company, location.hash]);

	if (isLoading)
		return (
			<Page>
				<div className="text-bgBase-800">Wird geladen …</div>
			</Page>
		);
	if (!company)
		return (
			<Page>
				<div className="text-bgBase-800">Unternehmen nicht gefunden</div>
			</Page>
		);

	return (
		<Page
			className="overflow-x-hidden"
			header={
				<PageHeader
					breadcrumbs={[
						{ label: 'CRM', to: '/crm' },
						{ label: 'Pipeline', to: '/crm/pipeline' },
						company.companyName ?? 'Unternehmen',
					]}
				>
					<div className="w-64">
						<CrmSearchBar />
					</div>
					<div className="flex items-center gap-2">
						{isAircallConnected && (
							<AircallDialDropdown
								company={company}
								contacts={contacts}
								dialLoading={dialLoading}
								onDial={handleAircallDial}
							/>
						)}
						{company.phoneNumber && !isAircallConnected && (
							<Button href={`tel:${company.phoneNumber}`} variant="outline" size="sm">
								<BsTelephone className="w-3 h-3" /> Anrufen
							</Button>
						)}
						{company.emailAddress && (
							<Button href={`mailto:${company.emailAddress}`} variant="outline" size="sm">
								<BsEnvelope className="w-3 h-3" /> E-Mail
							</Button>
						)}
						<Button onClick={() => setShowMeetingModal(true)} variant="outline" size="sm">
							<BsCalendar className="w-3 h-3" /> Meeting planen
						</Button>
						<div className="w-px h-5 bg-bgBase-500 mx-0.5" />
						<IconButton
							icon={linkCopied ? BsCheck : BsLink45Deg}
							label="Link kopieren"
							onClick={() => {
								navigator.clipboard?.writeText(window.location.href);
								setLinkCopied(true);
								setTimeout(() => setLinkCopied(false), 1500);
							}}
						/>
						<IconButton
							icon={BsTrash}
							label="Löschen"
							tone="danger"
							onClick={() => setCompanyDeleteOpen(true)}
						/>
					</div>
				</PageHeader>
			}
		>
			{backTo && (
				<div className="mb-3">
					<Button variant="outline" size="sm" onClick={() => navigate(backTo.to)}>
						<BsArrowLeft className="w-3 h-3" /> {backTo.label}
					</Button>
				</div>
			)}
			<CompanyHeader
				company={company}
				openDeals={openDeals}
				pipelineValue={pipelineValue}
				ownerMember={ownerMember}
			/>

			<DealsSection
				deals={deals}
				contacts={contacts}
				company={company}
				members={members}
				stagesByPipeline={stagesByPipeline}
				allProducts={allProducts}
				allBundles={allBundles}
				defaultStages={defaultStages}
				defaultPipeline={defaultPipeline}
				activeDealId={activeDealId}
				onActiveDealChange={setActiveDealId}
				onCreateDeal={handleCreateDeal}
				onDealUpdate={handleDealUpdate}
				onDealDelete={(deal) => setDealDeleteTarget(deal)}
				onStageTransition={handleStageTransition}
				onCreateContact={handleCreateContact}
			/>

			{/* Main grid: Left sidebar + Activity center */}
			<div
				className="grid gap-4 pb-8 items-start"
				style={{ gridTemplateColumns: 'minmax(260px, 300px) minmax(0, 1fr)' }}
			>
				<div className="flex flex-col gap-3">
					<CompanyInfoCard
						company={company}
						companyFieldDefs={companyFieldDefs}
						onSave={(patch) => companyMutation.mutate(patch)}
					/>
					<CompanyTagsCard company={company} allTags={allTags} />
					<ContactsCard
						contacts={contacts}
						companyId={companyId}
						highlightedContactId={highlightedContactId}
						aircallConnected={isAircallConnected}
						onCreateContact={handleCreateContact}
						onUpdateContact={handleContactUpdate}
						onDeleteContact={handleContactDelete}
						onAircallDial={handleAircallDial}
					/>
				</div>
				<div className="flex flex-col gap-4">
					{activeDeal && aiNextActionEnabled && <NextBestAction dealId={activeDeal.id} />}
					<div id="activities">
						<CompanyActivityPanel
							companyId={companyId}
							dealId={activeDeal?.id}
							contacts={contacts}
							companyEmail={company?.emailAddress}
						/>
					</div>
				</div>
			</div>

			{showNewActivity && (
				<NewActivityModal
					isOpen={showNewActivity}
					onClose={() => setShowNewActivity(false)}
					companyId={companyId}
					companyName={company?.companyName}
					dealId={activeDeal?.id}
					dealTitle={activeDeal?.title}
				/>
			)}

			{showMeetingModal && (
				<MeetingScheduleModal
					isOpen={showMeetingModal}
					onClose={() => setShowMeetingModal(false)}
					companyId={companyId}
					companyName={company?.companyName}
					dealId={activeDeal?.id}
				/>
			)}

			<ConfirmModal
				isOpen={Boolean(dealDeleteTarget)}
				onClose={() => setDealDeleteTarget(null)}
				onConfirm={async () => {
					setDealDeleting(true);
					try {
						await handleDealDelete(dealDeleteTarget.id);
						setDealDeleteTarget(null);
					} finally {
						setDealDeleting(false);
					}
				}}
				title={`Deal "${dealDeleteTarget?.title}" löschen`}
				message="Möchten Sie diesen Deal wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
				confirmText="Löschen"
				isLoading={dealDeleting}
			/>

			<ConfirmModal
				isOpen={companyDeleteOpen}
				onClose={() => setCompanyDeleteOpen(false)}
				onConfirm={handleCompanyDelete}
				title={`"${company?.companyName}" löschen`}
				message="Möchten Sie dieses Unternehmen wirklich in den Papierkorb verschieben?"
				subtitle="Es kann 30 Tage lang wiederhergestellt werden."
				confirmText="In Papierkorb"
				isLoading={softDeleteMutation.isPending}
			/>

			<LossReasonModal
				isOpen={Boolean(lossPrompt)}
				onClose={() => setLossPrompt(null)}
				onConfirm={confirmLossTransition}
				dealTitle={deals.find((d) => d.id === lossPrompt?.dealId)?.title}
				isLoading={lossSubmitting}
			/>
		</Page>
	);
};

export default CrmCompanyPage;
